import { useMemo } from 'react';
import { DataTable, EmptyState, StatusPill, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import { useInvoiceTransactions, type InvoiceTransactionRow } from '@/api/invoices';
import { formatMoney, formatDateTime } from '@/lib/format';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

const right = { textAlign: 'right' as const };

/** Map a transaction status to a semantic StatusPill tone. */
function txStatusTone(s?: string | null): StatusTone {
  const v = (s ?? '').toLowerCase();
  if (['success', 'completed', 'done', 'paid'].includes(v)) return 'success';
  if (['failed', 'cancelled', 'canceled', 'rejected'].includes(v)) return 'danger';
  if (['pending', 'waiting', 'processing'].includes(v)) return 'warning';
  return 'neutral';
}

/**
 * Transactions tab — payments recorded against the invoice in a DataTable: date,
 * method, amount (money right-aligned), note. Invoices with no payments render
 * an EmptyState.
 */
export function InvoiceTransactionsTab({ id, currency }: { id: number; currency?: string }) {
  const { data, isLoading, isError, refetch } = useInvoiceTransactions(id);
  const rows = data?.results ?? [];

  const columns = useMemo<ColumnDef<InvoiceTransactionRow, unknown>[]>(
    () => [
      {
        id: 'transaction_id',
        header: 'ID',
        cell: ({ row }) => {
          const tid = row.original.invoice_transaction_id ?? row.original.transaction_id;
          return <span style={{ color: 'var(--vn-fg-muted)' }}>{tid ?? '—'}</span>;
        },
      },
      {
        id: 'transaction_date',
        header: 'Date',
        cell: ({ row }) => formatDateTime(row.original.transaction_date ?? (row.original.created_at as string)),
      },
      {
        id: 'employee',
        header: 'By',
        cell: ({ row }) => row.original.employee_name || '—',
      },
      {
        id: 'transaction_method',
        header: 'Method',
        cell: ({ row }) => row.original.transaction_method || '—',
      },
      {
        id: 'transaction_amount',
        header: () => <div style={right}>Amount</div>,
        cell: ({ row }) => (
          <div style={{ ...right, fontWeight: 'var(--vn-font-weight-medium)' }}>
            {formatMoney(row.original.transaction_amount, currency)}
          </div>
        ),
      },
      {
        id: 'transaction_fee',
        header: () => <div style={right}>Fee</div>,
        cell: ({ row }) => {
          const f = row.original.transaction_fee;
          return <div style={right}>{f ? formatMoney(f, currency) : '—'}</div>;
        },
      },
      {
        id: 'exchange_rate',
        header: () => <div style={right}>Ex. Rate</div>,
        cell: ({ row }) => {
          const r = row.original.exchange_rate;
          return <div style={right}>{r != null && r !== 1 ? r.toLocaleString('en-US') : '—'}</div>;
        },
      },
      {
        id: 'transaction_status',
        header: 'Status',
        cell: ({ row }) => {
          const s = row.original.transaction_status;
          return s ? (
            <StatusPill tone={txStatusTone(s)}>{s}</StatusPill>
          ) : (
            <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>
          );
        },
      },
      {
        id: 'transaction_note',
        header: 'Note',
        cell: ({ row }) => (
          <span style={{ overflowWrap: 'anywhere', color: 'var(--vn-fg-muted)' }}>
            {row.original.transaction_note || '—'}
          </span>
        ),
      },
    ],
    [currency],
  );

  if (isError) return <TabError title="Failed to load transactions" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading />;

  return (
    <DataTable
      columns={columns}
      data={rows}
      enableSorting={false}
      getRowId={(r, i) => String(r.transaction_id ?? i)}
      emptyState={<EmptyState title="No transactions" description="No payments recorded for this invoice." />}
    />
  );
}
